// Tiny command registry shared between the toolbar buttons and the
// command palette. Commands are plain objects with a stable `id`, a
// human-readable `label`, an optional keyboard-friendly `shortcut`
// hint, and a `run` handler. Registration is order-preserving: the
// first matching command wins for both palette filtering and direct
// dispatch.
//
// Keep this module dependency-free so anything (toolbar, palette,
// keyboard shortcuts, lesson actions) can pull in a command by id
// without circular imports.

export interface Command {
  id: string;
  label: string;
  shortcut?: string;
  run(): void | Promise<void>;
}

const registry: Command[] = [];

export function register(cmd: Command): void {
  // Replace existing registration with the same id so reloads don't
  // accumulate duplicates during HMR.
  const idx = registry.findIndex((c) => c.id === cmd.id);
  if (idx >= 0) registry[idx] = cmd;
  else registry.push(cmd);
}

export function all(): readonly Command[] {
  return registry;
}

export function byId(id: string): Command | undefined {
  return registry.find((c) => c.id === id);
}

/// Substring + word-prefix fuzzy filter for the command palette.
/// Matches if every space-separated query word is a substring of the
/// command label (case-insensitive). Empty query returns everything.
export function filter(query: string): readonly Command[] {
  const q = query.trim().toLowerCase();
  if (q.length === 0) return registry;
  const words = q.split(/\s+/);
  return registry.filter((c) => {
    const haystack = c.label.toLowerCase();
    return words.every((w) => haystack.includes(w));
  });
}

export async function run(id: string): Promise<void> {
  const cmd = byId(id);
  if (!cmd) return;
  await cmd.run();
}
